import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  Card,
  Chip
} from '@mui/material';
import {
  PhoneCallback,
  WhatsApp,
  CheckCircle,
  Security,
  HeadsetMic
} from '@mui/icons-material';

interface ServiceContactFormProps {
  serviceCategory: string;
  selectedService: string;
  diagnosticAnswers?: Record<string | number, any>;
  selectedPackageTier?: string;
  onSuccess?: (leadId: string) => void;
  onBackToQuiz?: () => void;
}

export const ServiceContactForm: React.FC<ServiceContactFormProps> = ({
  serviceCategory,
  selectedService,
  diagnosticAnswers = {},
  selectedPackageTier,
  onSuccess,
  onBackToQuiz
}) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [contactMethod, setContactMethod] = useState<'callback' | 'whatsapp'>('callback');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedLeadId, setSubmittedLeadId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const WHATSAPP_NUMBER = '919116033575';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      setErrorMessage('Please enter a valid 10-digit mobile number.');
      return;
    }

    setIsSubmitting(true);

    try {
      const apiBase =
        process.env.REACT_APP_API_URL ||
        (typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
          ? 'http://localhost:5001'
          : 'https://fingard-backend.onrender.com');

      const fullServiceName = selectedPackageTier
        ? `${selectedService} (${selectedPackageTier.toUpperCase()} Package)`
        : selectedService;

      const response = await fetch(`${apiBase}/api/register-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientName: name.trim(),
          clientPhone: cleanPhone,
          clientEmail: email.trim().toLowerCase(),
          clientCity: city.trim(),
          serviceCategory: serviceCategory,
          selectedService: fullServiceName,
          diagnosticAnswers: diagnosticAnswers,
          contactPreference: contactMethod,
          pricingAmount: 0
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const leadId = data.leadId;
        setSubmittedLeadId(leadId);
        localStorage.setItem('fingard_lead_id', leadId);
        localStorage.setItem('fingard_client_name', name.trim());

        if (onSuccess) {
          onSuccess(leadId);
        }

        if (contactMethod === 'whatsapp') {
          const waMessage = `Hi Fingard Partners! I am inquiring about *${fullServiceName}*. My Lead Reference is *#${leadId}*. Name: ${name.trim()}, Phone: ${cleanPhone}. Please share the quotation and roadmap.`;
          const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(waMessage)}`;
          window.open(waUrl, '_blank');
        }
      } else {
        throw new Error(data.error || 'Unable to register inquiry. Please try again.');
      }
    } catch (err: any) {
      console.error('Lead registration error:', err);
      const fallbackId = `FG-${Date.now().toString().slice(-6)}`;
      setSubmittedLeadId(fallbackId);
      localStorage.setItem('fingard_lead_id', fallbackId);
      localStorage.setItem('fingard_client_name', name.trim());
      if (contactMethod === 'whatsapp') {
        const waMessage = `Hi Fingard Partners! I am inquiring about *${selectedService}*. Reference: *#${fallbackId}*. Name: ${name.trim()}.`;
        const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(waMessage)}`;
        window.open(waUrl, '_blank');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submittedLeadId) {
    const fullServiceName = selectedPackageTier
      ? `${selectedService} (${selectedPackageTier.toUpperCase()} Package)`
      : selectedService;
    const waMessage = `Hi Fingard Partners! I am inquiring about *${fullServiceName}*. My Lead Reference is *#${submittedLeadId}*. Name: ${name.trim()}. Please share the quotation.`;
    const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(waMessage)}`;

    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 5 },
          borderRadius: 4,
          border: '1px solid #E2E8F0',
          textAlign: 'center',
          bgcolor: '#FFFFFF',
          boxShadow: '0px 10px 30px rgba(15, 23, 42, 0.05)'
        }}
      >
        <Box sx={{ mb: 3 }}>
          <CheckCircle sx={{ fontSize: 72, color: '#10B981', mb: 1 }} />
          <Typography variant="h4" fontWeight={800} color="#0B2847" gutterBottom>
            Inquiry Registered Successfully!
          </Typography>
          <Chip
            label={`Reference Lead ID: #${submittedLeadId}`}
            sx={{
              bgcolor: '#EFF6FF',
              color: '#1E40AF',
              fontWeight: 700,
              fontSize: '0.95rem',
              py: 2,
              px: 1,
              borderRadius: 2
            }}
          />
        </Box>

        <Typography variant="body1" color="#475569" sx={{ maxWidth: 540, mx: 'auto', mb: 3, lineHeight: 1.7 }}>
          Thank you, <strong>{name}</strong>! Your diagnostic inquiry for <strong>{selectedService}</strong> has been logged with our Senior CA Advisory Desk.
        </Typography>

        <Paper
          variant="outlined"
          sx={{
            p: 3,
            maxWidth: 540,
            mx: 'auto',
            mb: 4,
            borderRadius: 3,
            bgcolor: '#F8FAFC',
            textAlign: 'left'
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} color="#0B2847" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <HeadsetMic sx={{ fontSize: 20, color: '#123D6B' }} />
            What happens next?
          </Typography>
          <Box component="ol" sx={{ pl: 2.5, m: 0, '& li': { mb: 1, color: '#475569', fontSize: '0.9rem' } }}>
            <li>
              <strong>Senior Consultant Review:</strong> Our team reviews your requirements and prepares a customized, transparent price quote.
            </li>
            <li>
              <strong>Direct Connect:</strong> {contactMethod === 'whatsapp' ? 'We are connecting with you on WhatsApp' : 'Our advisor will call you on your phone'} within <strong>30 minutes</strong> (Mon-Sat, 9AM-7PM).
            </li>
            <li>
              <strong>Secure Payment & Document Vault:</strong> After discussing your needs, you will receive a secure payment link. Once paid, your Document Vault unlocks immediately to securely upload your files.
            </li>
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<WhatsApp />}
            component="a"
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              py: 1.4,
              px: 3.5,
              bgcolor: '#25D366',
              color: '#FFFFFF',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 2.5,
              '&:hover': { bgcolor: '#1EBE5D' }
            }}
          >
            Chat with Sales on WhatsApp
          </Button>

          {onBackToQuiz && (
            <Button
              variant="outlined"
              onClick={onBackToQuiz}
              sx={{
                py: 1.4,
                px: 3,
                borderColor: '#CBD5E1',
                color: '#475569',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2.5
              }}
            >
              Start Another Inquiry
            </Button>
          )}
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, md: 4.5 },
        borderRadius: 4,
        border: '1px solid #E2E8F0',
        bgcolor: '#FFFFFF',
        boxShadow: '0px 10px 30px rgba(15, 23, 42, 0.04)'
      }}
    >
      <Box sx={{ textAlign: 'center', mb: 3.5 }}>
        <Chip
          label="Custom Quotation & Consultation"
          color="primary"
          size="small"
          sx={{ mb: 1, fontWeight: 700, bgcolor: '#123D6B' }}
        />
        <Typography variant="h5" fontWeight={800} color="#0B2847" gutterBottom>
          Connect with Our Advisory Team
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto' }}>
          We evaluate each filing individually to offer transparent, tailored pricing with no hidden charges.
        </Typography>
      </Box>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Full Name *"
              placeholder="e.g. Anish Sharma"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              variant="outlined"
              size="medium"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="WhatsApp Mobile Number *"
              placeholder="10-digit mobile number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              variant="outlined"
              size="medium"
              helperText="For consultation and direct payment link"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email Address *"
              placeholder="anish@example.com"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              variant="outlined"
              size="medium"
              helperText="Quotation and official receipt will be sent here"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="City / State"
              placeholder="e.g. Jaipur, Rajasthan"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              variant="outlined"
              size="medium"
            />
          </Grid>

          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} color="#0B2847" sx={{ mb: 1.5 }}>
              How would you prefer our sales team to connect with you?
            </Typography>

            <RadioGroup
              value={contactMethod}
              onChange={(e) => setContactMethod(e.target.value as 'callback' | 'whatsapp')}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Card
                    variant="outlined"
                    onClick={() => setContactMethod('callback')}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      borderRadius: 3,
                      borderColor: contactMethod === 'callback' ? '#123D6B' : '#E2E8F0',
                      borderWidth: contactMethod === 'callback' ? 2 : 1,
                      bgcolor: contactMethod === 'callback' ? 'rgba(18, 61, 107, 0.04)' : '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <FormControlLabel
                      value="callback"
                      control={<Radio sx={{ color: '#123D6B', '&.Mui-checked': { color: '#123D6B' } }} />}
                      label=""
                      sx={{ m: 0 }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <PhoneCallback sx={{ color: '#123D6B', fontSize: 26 }} />
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700} color="#0B2847">
                          Request a Callback
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Senior CA calls you in 30 mins
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Card
                    variant="outlined"
                    onClick={() => setContactMethod('whatsapp')}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      borderRadius: 3,
                      borderColor: contactMethod === 'whatsapp' ? '#25D366' : '#E2E8F0',
                      borderWidth: contactMethod === 'whatsapp' ? 2 : 1,
                      bgcolor: contactMethod === 'whatsapp' ? 'rgba(37, 211, 102, 0.06)' : '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <FormControlLabel
                      value="whatsapp"
                      control={<Radio sx={{ color: '#25D366', '&.Mui-checked': { color: '#25D366' } }} />}
                      label=""
                      sx={{ m: 0 }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <WhatsApp sx={{ color: '#25D366', fontSize: 28 }} />
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700} color="#0B2847">
                          Chat on WhatsApp
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Instant chat with sales rep
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </RadioGroup>
          </Grid>

          <Grid item xs={12} sx={{ mt: 2 }}>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={22} color="inherit" />
                ) : contactMethod === 'whatsapp' ? (
                  <WhatsApp />
                ) : (
                  <PhoneCallback />
                )
              }
              sx={{
                py: 1.6,
                borderRadius: 2.5,
                bgcolor: contactMethod === 'whatsapp' ? '#25D366' : '#123D6B',
                fontWeight: 700,
                fontSize: '1rem',
                textTransform: 'none',
                '&:hover': {
                  bgcolor: contactMethod === 'whatsapp' ? '#1EBE5D' : '#0B2847'
                }
              }}
            >
              {isSubmitting
                ? 'Registering Inquiry...'
                : contactMethod === 'whatsapp'
                ? 'Get Custom Quote via WhatsApp →'
                : 'Request Callback for Custom Quote →'}
            </Button>
          </Grid>
        </Grid>
      </form>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 3, color: '#64748B' }}>
        <Security sx={{ fontSize: 16 }} />
        <Typography variant="caption">
          Strict confidentiality guaranteed. No obligation, 100% free preliminary consultation.
        </Typography>
      </Box>

      {onBackToQuiz && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button
            variant="text"
            onClick={onBackToQuiz}
            size="small"
            sx={{ textTransform: 'none', color: '#64748B' }}
          >
            ← Back to Questionnaire
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default ServiceContactForm;
